import { useEffect, useRef } from "react";
import { useToast } from "../../../shared/ui";
import { defaultIndexOf } from "../../../features/vault/configurator";
import { libraryItemId } from "../../../features/vault/applyOption";
import { useDomainState, useUi } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { SlotBar } from "../../configurator/SlotBar";
import { choicesOf } from "../../configurator/swatch";
import { partName } from "../basic/rail/usePartScope";
import { RAIL_W, RIGHT_PANEL_W, type ChromeInsets } from "../layout";
import { useStudioModelId } from "../studioModel";
import { useCustomerLink } from "./customerLink";
import { OPEN_CONFIGURATOR_DETAILS_EVENT } from "./draft";
import { OptionPicker } from "./OptionPicker";
import { showsWrongKind, useConfiguratorEditing, type LiveSlot } from "./useConfiguratorEditing";

/**
 * The customer configurator, on the canvas — iJewel's material bar.
 *
 * Along the bottom, one pill per part of the piece ("Metal 01", "Gem 01");
 * above it the active part's choices as round swatches, a "+" that opens the
 * material library beside the canvas, and an × on each choice. Clicking a
 * choice shows it on the model (one Undo step) and makes it the one the part
 * opens on. Save writes the choices to the Vault model; the shared link then
 * shows the same bar to shoppers (`app/ConfiguratorView.tsx`).
 *
 * Shown and hidden from the top bar's "Configurator" button (in Basic and in
 * Advanced alike), from Basic › Share's switch, or ⌘K — all one flag on the
 * per-editor draft. A Vault model that already has choices opens with it on.
 * After the pills: Save, "Preview" (the shopper's own view over the canvas,
 * `CustomerPreview`), "Open customer link" on a Vault model, and × to hide.
 *
 * Each part's kind is the SLOT's (`slotKindOf`: its name, else as it loaded),
 * so "Gem 01" stays a stone slot even while the stones are showing as metal —
 * and when they are, a line above the bar says so and puts them back.
 *
 * Stacked above the view bar (Hero ¾ · Front · … · Fit) in Basic, centred on
 * the part of the canvas the panels leave visible, like it.
 */

/** The view bar's own box: 26px off the bottom, 42px tall, plus a 10px gap. */
const ABOVE_VIEW_BAR = 26 + 42 + 10;
const BOTTOM_GAP = 26;

const barButton =
  "inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-[13px] font-medium text-ed-text-2 transition-colors hover:bg-ed-panel hover:text-ed-text disabled:cursor-default disabled:opacity-50";
const barIcon = "grid h-8 w-8 shrink-0 place-items-center rounded-full text-ed-text-2 hover:bg-ed-panel hover:text-ed-text";

export function ConfiguratorBar({ insets }: { insets: ChromeInsets }) {
  const { state, draft, slots, busy, engineBusy, addFromLibrary, choose, remove, fixKind } = useConfiguratorEditing();
  const modelId = useStudioModelId();
  const models = useDomainState("models");
  const [ui, patchUi] = useUi();
  const { toast } = useToast();
  const link = useCustomerLink();

  useEffect(() => draft.bindModel(modelId), [draft, modelId]);
  // Harnesses read the draft the way they read `__editorStore`. Dev builds only.
  useEffect(() => {
    if (import.meta.env.DEV) (window as unknown as { __configuratorDraft: typeof draft }).__configuratorDraft = draft;
  }, [draft]);

  // /dev/studio: choices are local until saved, and they describe the piece
  // they were made on. Loading a different piece starts again.
  const modelKey = models.map((m) => m.id).join("|");
  const lastModelKey = useRef(modelKey);
  useEffect(() => {
    if (!modelId && lastModelKey.current && modelKey && lastModelKey.current !== modelKey) draft.reset();
    lastModelKey.current = modelKey;
  }, [draft, modelId, modelKey]);

  // Esc closes the picker, as it does every other panel.
  useEffect(() => {
    if (!state.pickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") draft.setPickerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.pickerOpen, draft]);

  // The "+" picker sits exactly over Fine-tune in Basic. Moving to another
  // left-rail panel means the user has gone elsewhere: close it, so it cannot
  // linger over Fine-tune and read as "the panel vanished".
  const lastLeft = useRef(ui.leftPanel);
  useEffect(() => {
    if (lastLeft.current !== ui.leftPanel && state.pickerOpen) draft.setPickerOpen(false);
    lastLeft.current = ui.leftPanel;
  }, [ui.leftPanel, state.pickerOpen, draft]);

  if (!state.on || slots.length === 0) return null;

  const active: LiveSlot = slots.find((s) => s.group.id === state.activeSlot) ?? slots[0];
  const options = active.config?.options ?? [];
  const opensOn = active.config ? defaultIndexOf(active.config) : -1;
  const loading = state.load === "loading";
  const basic = ui.inspectorMode === "basic";
  const dirty = draft.dirty;
  const nameOf = (s: LiveSlot) => partName(s.group, s.index, s.kind);
  // The part to put right: the one on screen if it is wrong, else the first that is.
  const wrong = showsWrongKind(active) ? active : slots.find(showsWrongKind);

  const save = async () => {
    try {
      const result = await draft.save();
      if (result === "no-model") {
        toast({
          tone: "warning",
          title: "Save this piece to your Vault first",
          description:
            "Customer choices are saved with the piece, so it needs to be in your Vault before a shopper can see them. They stay here until you reload.",
        });
        return;
      }
      toast({ tone: "success", title: "Customer choices saved", description: "Your shared link shows them now." });
    } catch {
      toast({ tone: "error", title: "Could not save the customer choices", description: "Nothing was saved. Try again." });
    }
  };

  const saveLabel = state.saving ? "Saving…" : dirty || !modelId ? "Save" : "Saved";

  return (
    <>
      <div
        className="pointer-events-none absolute z-20 flex flex-col items-center gap-2 px-3"
        style={{ left: insets.left, right: insets.right, bottom: basic ? ABOVE_VIEW_BAR : BOTTOM_GAP }}
        data-config-studio
      >
        {wrong ? (
          <p
            className="pointer-events-auto flex max-w-full items-center gap-2 rounded-full border border-ed-border bg-ed-panel py-1.5 pl-3 pr-1.5 text-[12px] leading-4 text-ed-text shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
            role="status"
            data-config-kind-notice={wrong.group.id}
          >
            <Icon name="error" size={16} className="shrink-0 text-ed-accent" />
            <span className="min-w-0 truncate">
              {wrong.kind === "gem"
                ? wrong === active
                  ? "These stones are showing as metal"
                  : `${nameOf(wrong)}: the stones are showing as metal`
                : wrong === active
                  ? "This metal part is showing as a stone"
                  : `${nameOf(wrong)} is showing as a stone`}
            </span>
            <span aria-hidden className="text-ed-text-3">—</span>
            <button
              type="button"
              onClick={() => void fixKind(wrong)}
              disabled={engineBusy}
              className="shrink-0 rounded-full px-2 py-0.5 font-semibold text-ed-accent hover:underline disabled:opacity-60"
              data-config-kind-fix
            >
              {wrong.kind === "gem" ? "Make them stones again" : "Make it metal again"}
            </button>
          </p>
        ) : null}
        <SlotBar
          tone="studio"
          ariaLabel="Parts customers can change"
          slots={slots.map((s) => ({
            key: s.group.id,
            label: nameOf(s),
            title: s.group.label,
          }))}
          activeSlot={active.group.id}
          onSlot={(key) => draft.setActiveSlot(key)}
          choices={choicesOf(options, opensOn, busy)}
          onChoice={(key) => void choose(active, key)}
          onAdd={() => draft.setPickerOpen(!state.pickerOpen)}
          addOpen={state.pickerOpen}
          onRemove={(key) => remove(active, key)}
          disabled={loading}
          emptyText="Add the materials customers can pick for this part"
          // The ring marks what a shopper opens on, which after a reload need
          // not be what the model is wearing in the studio — say so.
          activeCaption={(name) => `${name} · shown first`}
          trailing={
            <>
              <span className="mx-0.5 h-5 w-px shrink-0 bg-ed-border" />
              {modelId ? (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event(OPEN_CONFIGURATOR_DETAILS_EVENT))}
                  title="Prices, names and model choices"
                  aria-label="Prices, names and model choices"
                  className={barIcon}
                  data-config-details
                >
                  <Icon name="sell" size={17} />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void save()}
                disabled={state.saving || loading || (!!modelId && !dirty)}
                className={`inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3.5 text-[13px] font-semibold transition-colors disabled:cursor-default ${
                  dirty ? "bg-ed-text text-white hover:bg-black" : "text-ed-text-2 hover:text-ed-text disabled:hover:text-ed-text-2"
                }`}
                title={modelId ? "Save the customer choices to this piece" : "Customer choices are saved with a piece in your Vault"}
                data-config-save
              >
                {saveLabel === "Saved" ? <Icon name="check" size={15} /> : null}
                {saveLabel}
              </button>
              <button
                type="button"
                onClick={() => {
                  draft.setPickerOpen(false);
                  patchUi({ customerPreview: true });
                }}
                disabled={loading}
                title="See the piece the way a shopper sees it on your link"
                className={barButton}
                data-config-preview
              >
                <Icon name="visibility" size={17} />
                Preview
              </button>
              {link.available ? (
                <button
                  type="button"
                  onClick={link.open}
                  disabled={loading || state.saving}
                  title="Open your shared link in a new tab (saves your choices first)"
                  aria-label="Open customer link"
                  className={barIcon}
                  data-config-open-link
                >
                  <Icon name="open_in_new" size={17} />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => draft.setOn(false)}
                title="Hide the configurator (your choices stay)"
                aria-label="Hide the configurator"
                className={barIcon}
                data-config-hide
              >
                <Icon name="close" size={18} />
              </button>
            </>
          }
        />
      </div>

      {state.pickerOpen ? (
        <aside
          className="pointer-events-auto absolute bottom-0 top-0 z-30 flex flex-col border-l border-ed-border bg-ed-panel shadow-[-12px_0_32px_rgba(0,0,0,0.08)]"
          style={{ width: RIGHT_PANEL_W, right: basic ? 0 : RAIL_W }}
          aria-label={`Add a choice for ${nameOf(active)}`}
          data-config-picker
        >
          <header className="flex items-start gap-2 px-[18px] pb-3 pt-4">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[15px] font-semibold leading-5 text-ed-text">Add to {nameOf(active)}</h2>
              <p className="mt-0.5 text-[12px] leading-4 text-ed-text-2">
                Each material you pick becomes a choice customers can switch to.
              </p>
            </div>
            <button
              type="button"
              onClick={() => draft.setPickerOpen(false)}
              title="Close"
              aria-label="Close"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"
              data-config-picker-close
            >
              <Icon name="close" size={18} />
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pb-5">
            <OptionPicker
              // A different part starts on ITS kind's tab, with no question pending.
              key={active.group.id}
              kind={active.kind}
              activeId={options[opensOn] ? libraryItemId(options[opensOn].id) : null}
              onPick={(item) => addFromLibrary(active, item)}
            />
          </div>
          {engineBusy ? <span className="sr-only" aria-live="polite">Applying…</span> : null}
        </aside>
      ) : null}
    </>
  );
}
